// Libraries
import * as React from "react";
import { observer } from "mobx-react";
import {
  AirportMarker,
  GoogleMapCoordinates,
  UitkMapPlaceCardProps,
  Marker,
  MarkerProps,
  PlaceMarker,
  MapPlace,
  TransitMarker,
  UitkMap,
  MapInteractionCallback,
} from "uitk-react-experimental-module-map";
// Utils
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent,
  sendImmediateTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
// Types
import { GeoType } from "src/typings/microserviceModels/maps-flex-module";
import { LocationType } from "src/components/utility/LocationUtils";
import { MapContextType, ModuleMapProps, PoiForMap } from "./typings";
import { PoiStore } from "src/stores/poiStore/PoiStore";
import MapPoiSheet from "./components/MapPoiSheet";
import { Experiment, isVariantEnabled } from "src/components/utility/experiment";
import { formatText } from "bernie-l10n";

type MapPropertyAndPOICardProps = UitkMapPlaceCardProps & {
  place: MapPlace | PoiForMap;
};

export const MapContext = React.createContext({});

export const useMapContext = (): MapContextType => {
  const context: React.Context<MapContextType> | {} = React.useContext(MapContext);

  if (!context || Object.keys(context).length === 0) {
    throw new Error(`Map compound components cannot be rendered outside the Map component`);
  }

  return context as MapContextType;
};

const usePoisPins = (
  regionId: string,
  locale: string,
  poiStore: PoiStore,
  isPoiExperimentEnabled: boolean,
  limit: number
) => {
  React.useEffect(() => {
    const tcsLocale = locale.toUpperCase();
    if (isPoiExperimentEnabled && regionId && !poiStore.getPois().length) {
      poiStore.fetchPoisForRegion(regionId.toString(), tcsLocale, limit);
    }
  }, [regionId, poiStore]);
};

/**
 * Main Component Map. Here is rendered the map component.
 * @param {ModuleMapProps} props
 */

const clusterIcon = (count: number, price: string) => {
  return (
    <div className="default uitk-type-200">
      <div
        className="uitk-marker-cluster"
        dangerouslySetInnerHTML={{
          __html: formatText("maps.markers.cluster.available.label", count, price),
        }}
      />
    </div>
  );
};

const displayCard = (onHotelSelect: (selectedProperty: MapPlace | PoiForMap) => MapInteractionCallback | void) => ({
  place,
  isVisible,
}: MapPropertyAndPOICardProps) => {
  const iconType = place.icon && place.icon.type;

  switch (iconType) {
    case "price":
    case "soldOut":
      return <UitkMap.PlaceCard onHotelSelect={onHotelSelect} place={place as MapPlace} isVisible={isVisible} />;
    default:
      return (
        <MapPoiSheet
          id={(place as PoiForMap).id}
          title={(place as PoiForMap).name}
          photoUrl={(place as PoiForMap).imageUrl}
          description={(place as PoiForMap).description}
          isVisible={isVisible}
        />
      );
  }
};

export const ModuleMap: React.FC<ModuleMapProps> = observer((props: ModuleMapProps) => {
  const { toggleShowFullscreenMap, sendUserToHSR, shouldOpenMapInHSR } = useMapContext();
  const [isVisibleErrorMessage, setIsVisibleErrorMessage] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const location = props.flexComponent.model.location ?? props.context!.searchContext.location;
  const geoCoordinate = location?.geoCoordinate;
  const isPoiExperimentEnabled = isVariantEnabled(props.context, Experiment.HTG_Add_Top_POI_Pins);
  const pois = props.poi && props.poi.getPois();
  const isMobile = props.context && props.context.deviceInformation && props.context.deviceInformation.mobile;

  usePoisPins(location?.id.toString(), props.context.locale, props.poi, isPoiExperimentEnabled, isMobile ? 5 : 10);

  function onMapMounted(google: any, map: google.maps.Map) {
    if (props.isDynamic) {
      map.setOptions({ scrollwheel: false });
    }

    if (props.onMapMounted) {
      props.onMapMounted();
    }

    if (geoCoordinate && geoCoordinate.latitude && geoCoordinate.longitude) {
      initMarkerBasedOnLocationType(location?.type, {
        googleMaps: google,
        map,
        position: {
          lat: geoCoordinate.latitude,
          lng: geoCoordinate.longitude,
        },
        label: "",
        id: location?.id,
      });
    }
  }

  const initMarkerBasedOnLocationType = (locationType: GeoType, markerProps: MarkerProps): Marker | null => {
    switch (locationType) {
      case LocationType.POINT_OF_INTEREST:
        return new PlaceMarker({ ...markerProps });
      case LocationType.AIRPORT:
        return new AirportMarker({ ...markerProps });
      case LocationType.TRAIN_STATION:
      case LocationType.METRO_STATION:
        return new TransitMarker({ ...markerProps });
      default:
        return null;
    }
  };

  const mapTracking = (rfrrString: string) => {
    const flexAnalyticsInfo: FlexTrackingInfo = {
      moduleName: props.flexComponent.name,
      action: Action.CLICK,
      rfrr: rfrrString,
    };
    sendDelayedTrackEvent(flexAnalyticsInfo, props.analytics);
  };

  const doSearch = async (mapCenter: GoogleMapCoordinates) => {
    mapTracking("fullscreen.map.search");
    setIsLoading(true);

    if (!props.context || !mapCenter || !mapCenter.lat || !mapCenter.lng) {
      // This shouldn't happen, but let's make sure!
      setIsVisibleErrorMessage(true);
      setIsLoading(false);

      return;
    }

    try {
      await props.hotels.fetchHotelsByLatLong(props.context, mapCenter.lat, mapCenter.lng, location?.id.toString(10));
    } catch (err) {
      setIsVisibleErrorMessage(true);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const closeErrorMessage = () => {
    setIsVisibleErrorMessage(false);
  };

  const toolbarHeader = location?.extendedName || "";

  const onMapPan = ({ lat, lng }: GoogleMapCoordinates) => {
    mapTracking("fullscreen.map.pan");
  };

  const onClickMarker = (index: number, marker: MapPlace | PoiForMap) => {
    if (marker.icon?.type === "place") {
      mapTracking(`fullscreen.marker.poi`);
    } else {
      mapTracking(`fullscreen.marker.${index}`);
    }
  };

  const onMapZoomIn = () => {
    mapTracking("fullscreen.map.zoom.in");
  };

  const onMapZoomOut = () => {
    mapTracking("fullscreen.map.zoom.out");
  };

  const onHotelSelect = () => {
    const flexAnalyticsInfo: FlexTrackingInfo = {
      moduleName: props.flexComponent.name,
      action: Action.CLICK,
      rfrr: "fullscreen.hotel-card",
    };
    sendImmediateTrackEvent(flexAnalyticsInfo, props.analytics);
  };

  if (!props.hotels.allHotelsOnMap.length) {
    return null;
  }

  const getMarkers = () =>
    pois ? ([...props.hotels.allHotelsOnMap, ...pois] as MapPlace[]) : props.hotels.allHotelsOnMap;

  return (
    <UitkMap
      isVisible={true}
      locale={props.context.locale}
      countryCode={props.context?.site?.country}
      toolbarHeader={toolbarHeader}
      toolbarIconLabel={formatText("maps.fullscreen.close.a11yLabel")}
      places={getMarkers()}
      onClose={shouldOpenMapInHSR ? sendUserToHSR : toggleShowFullscreenMap}
      onMapMounted={onMapMounted}
      onPan={onMapPan}
      onZoomIn={onMapZoomIn}
      onZoomOut={onMapZoomOut}
      onClickPlaceMarker={onClickMarker}
      isDynamic={props.isDynamic}
      className={props.classNames}
    >
      <UitkMap.Overlay>
        <UitkMap.SearchButton
          isSearchInProgress={isLoading}
          searchMessage={formatText("maps.fullscreen.searchThisArea")}
          onMapSearch={doSearch}
        />
        <UitkMap.Loader isLoading={isLoading} />
        <UitkMap.ErrorMessage
          isVisible={isVisibleErrorMessage}
          onDismiss={closeErrorMessage}
          heading={formatText("maps.filter.error.areaError.header")}
          message={formatText("maps.filter.error.areaError.message")}
        />
      </UitkMap.Overlay>
      <UitkMap.PlaceCard>{displayCard(onHotelSelect)}</UitkMap.PlaceCard>
      <UitkMap.ClusteringMarkers clusterIcon={clusterIcon} minimumClusterSize={3} clusteringMarkerType={["price"]} />
    </UitkMap>
  );
});

ModuleMap.displayName = "ModuleMap";

export default ModuleMap;
